<template>
  <modal v-bind="$attrs" v-on="$listeners" :center="true" width="688px" id="add-study-set-modal">
    <template v-slot:title>
      <h1>{{ $t('study_sets.modals.addAssignment.add_study_set') }}</h1>
    </template>
    <template v-slot:body>
      <div id="add-study-set-modal-body" v-loading="loading">
        <el-form>
          <elsa-search v-model="queryString" @search="filterStudySetLazyLoad" />
        </el-form>
        <ul v-if="useLazyLoad" v-infinite-scroll="loadStudySet" class="study-set-overview-list">
          <li v-for="studySet in searchableStudySets" :key="studySet.id" class="study-set-overview-row-wrapper">
            <el-row type="flex" :gutter="0" class="study-set-overview">
              <el-col :span="1">
                <el-checkbox size="small" class="elsa-checkbox" v-model.lazy="studySet.checked"> </el-checkbox>
              </el-col>
              <el-col :span="1"><font-awesome-icon
                  :icon="!isStudySetShowDetail(studySet.id) ? 'chevron-down' : 'chevron-up'"
                  @click="toggleShowDetailStudySets(studySet.id)" /></el-col>
              <el-col :span="7">
                <p class="study-set-name">{{ studySet.name }}</p>
              </el-col>
              <el-col :span="6">
                <p>
                  <span class="label">{{ $t('study_sets.overview.category') }}:</span> <span class="value">{{
                    getCategoryLabel(studySet.category) }}</span>
                </p>
              </el-col>
              <el-col :span="3">
                <p>
                  <span class="label">{{ $t('study_sets.overview.phrases_count') }}:</span> <span class="value">{{
                    studySet.phrases_count }}</span>
                </p>
              </el-col>
              <el-col :span="6">
                <p>
                  <span class="label">{{ $t('study_sets.overview.created_by') }}:</span> <span class="value">{{
                    studySet.owner_name }}</span>
                </p>
              </el-col>
            </el-row>
            <el-collapse-transition>
              <div v-show="isStudySetShowDetail(studySet.id)" class="phrases-wrapper">
                <ul class="phrases-list">
                  <li v-for="(phrase, index) in showPharseOfStudySet(studySet)" :key="index">
                    <el-row type="flex" :gutter="24" width="100%">
                      <el-col :offset="2" :span="20">
                        {{ phrase }}
                      </el-col>
                    </el-row>
                  </li>
                </ul>
              </div>
            </el-collapse-transition>
          </li>
        </ul>

        <ul v-else class="study-set-overview-list">
          <li v-for="studySet in searchableStudySets" :key="studySet.id" class="study-set-overview-row-wrapper">
            <el-row type="flex" :gutter="0" class="study-set-overview">
              <el-col :span="1">
                <el-checkbox size="small" class="elsa-checkbox" v-model.lazy="studySet.checked"
                  @change="(status) => changeStudySetChecked(studySet, status)" :checked="checkChecked(studySet)">
                </el-checkbox>
              </el-col>
              <el-col :span="1"><font-awesome-icon
                  :icon="!isStudySetShowDetail(studySet.id) ? 'chevron-down' : 'chevron-up'"
                  @click="toggleShowDetailStudySets(studySet.id)" /></el-col>
              <el-col :span="7">
                <p class="study-set-name">{{ studySet.name }}</p>
              </el-col>
              <el-col :span="6">
                <p>
                  <span class="label">{{ $t('study_sets.overview.category') }}:</span> <span class="value">{{
                    getCategoryLabel(studySet.category) }}</span>
                </p>
              </el-col>
              <el-col :span="3">
                <p>
                  <span class="label">{{ $t('study_sets.overview.phrases_count') }}:</span> <span class="value">{{
                    studySet.phrases_count }}</span>
                </p>
              </el-col>
              <el-col :span="6">
                <p>
                  <span class="label">{{ $t('study_sets.overview.created_by') }}:</span> <span class="value">{{
                    studySet.owner_name }}</span>
                </p>
              </el-col>
            </el-row>
            <el-collapse-transition>
              <div v-show="isStudySetShowDetail(studySet.id)" class="phrases-wrapper">
                <ul class="phrases-list">
                  <li v-for="(phrase, index) in showPharseOfStudySet(studySet)" :key="index">
                    <el-row type="flex" :gutter="24" width="100%">
                      <el-col :offset="2" :span="20">
                        {{ phrase }}
                      </el-col>
                    </el-row>
                  </li>
                </ul>
              </div>
            </el-collapse-transition>
          </li>
        </ul>

        <div v-if="!useLazyLoad" class="footer-pagination">
          <over-table-pagination class="pagination__description" :currentPage="pagination.currentPage" :pageSize="50"
            :total="studySetCount" :formatter="overtablePaginationFormatter" />
          <pagination class="pagination__control" :page-size="50" :total="studySetCount"
            :current-page.sync="pagination.currentPage" v-on:current-change="onPaginationChange" :pager-counter="7" />
          <div class="pagination__space"></div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <tertiary-button class="confirm-cancel" @click.native="handleClose">
        {{ $t('common.cancel') }}
      </tertiary-button>
      <primary-button class="confirm-delete" ref="" @click.native="handleAdd">
        {{ $t('common.add') }}
      </primary-button>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/common/Modal'
import PrimaryButton from '@/components/common/buttons/PrimaryButton'
import TertiaryButton from '@/components/common/buttons/TertiaryButton'
import ElsaCheckbox from '@/components/common/Checkbox'
import ElsaSearch from '@/components/studysets/Search'
import { DefaultShareableOptions, DefaultCategoryOptions } from '@/constants/studySets'
import { getOptionsLabels } from '@/utils/elementUi'
import OverTablePagination from '@/components/common/v2/OverTablePagination'
import Pagination from '@/components/common/Pagination'

export default {
  components: {
    Modal,
    PrimaryButton,
    TertiaryButton,
    ElsaSearch,
    OverTablePagination,
    Pagination
  },
  props: {
    assignedStudySetIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      showDetailStudySets: [],
      queryString: null,
      searchResultStudySet: [],
      studySetCount: 0,
      search: {
        type: String,
        default: ''
      },
      useLazyLoad: false,
      lazyLoadStudySet: {
        nextPage: '',
        currentPage: 1
      },
      pagination: {
        nextPage: '',
        currentPage: 1
      },
      listChecked: []
    }
  },
  computed: {
    organizationId() {
      return this.$store.state.organization.currentOrganizationId
    },
    getTitle() {
      return this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, 'modals.delete.title'), {
        numberOfClasses: this.numberOfSelectedClasses
      })
    },
    searchableStudySets() {
      return this.searchResultStudySet.filter((x) => !this.assignedStudySetIds.includes(x.id))
    }
  },
  methods: {
    handleAdd() {
      this.$emit('closed')
      this.$emit('add', this.listChecked)
      this.listChecked = []
      // this.$emit(
      //   'add',
      //   this.searchableStudySets.filter((x) => x.checked)
      // )
      // for (let i = 0; i < this.searchResultStudySet.length; i++) {
      //   delete this.searchResultStudySet[i].checked
      // }
    },
    handleClose() {
      this.$emit('closed')
      for (let i = 0; i < this.searchResultStudySet.length; i++) {
        delete this.searchResultStudySet[i].checked
      }
    },
    reset() {
      //
    },
    getCategoryLabel(v) {
      return getOptionsLabels(DefaultCategoryOptions, v)
    },
    async filterStudySet(search) {
      this.searchResultStudySet = [];
      await this.getStudySet({ search })
      this.searchResultStudySet = this.searchResultStudySet.filter((item) => {
        return search.toLowerCase().split(' ').every(v => item.name.toLowerCase().includes(v))
      })
    },
    searchStudySet(search) {
      return this.getStudySet({ search })
    },

    getStudySetPage(page) {
      const search = this.queryString
      return this.getStudySet({ page, search })
    },
    async getStudySet({ page = 1, search = null, status = '' }) {
      this.loading = true
      let params = { page, search, status }
      try {
        const { data } = await this.$axios.get(`/v1/study-sets/${this.organizationId}/`, { params })
        this.searchResultStudySet = this.searchResultStudySet.concat(data.results)
        this.studySetCount = data.count
        if (page <= Math.round(this.studySetCount / 50)) {
          let pageNext = page + 1
          this.getStudySetPage(pageNext)
        }
      } catch (error) {
        // error
      }
      this.loading = false
    },
    loadStudySet() {
      this.getStudySetLazyLoad({})
    },
    loadPaginationStudySet() {
      this.getStudySetPagination({})
    },
    onPaginationChange(page) {
      this.getStudySetPagination({ page })
    },
    async getStudySetLazyLoad({ status = '' }) {
      if (!this.lazyLoadStudySet.currentPage) return

      this.loading = true
      let params = { page: this.lazyLoadStudySet.currentPage, search: this.queryString || '', status }
      try {
        const { data } = await this.$axios.get(`/v1/study-sets/${this.organizationId}/`, { params })
        this.studySetCount = data.count
        this.searchResultStudySet = this.searchResultStudySet.concat(data.results)
        this.lazyLoadStudySet.nextPage = data.next
        if (data.next) {
          this.lazyLoadStudySet.currentPage += 1
        } else {
          this.lazyLoadStudySet.currentPage = null
        }
      } catch (error) {
        // error
      }
      this.loading = false
    },
    async getStudySetPagination({ status = '', page = 1 }) {
      if (!this.pagination.currentPage) return

      this.loading = true
      let params = { page: this.pagination.currentPage, search: this.queryString || '', status }
      try {
        const { data } = await this.$axios.get(`/v1/study-sets/${this.organizationId}/`, { params })
        this.studySetCount = data.count
        this.searchResultStudySet = data.results
        this.pagination.nextPage = data.next

        //old logic
        // if (data.next) {
        //   this.pagination.currentPage += 1
        // } else {
        //   this.pagination.currentPage = null
        // }
      } catch (error) {
        // error
      }
      this.loading = false
    },
    async filterStudySetLazyLoad() {
      this.searchResultStudySet = [];
      this.lazyLoadStudySet.currentPage = 1
      this.getStudySetLazyLoad({})
    },
    isStudySetShowDetail(id) {
      return this.showDetailStudySets.includes(id)
    },
    toggleShowDetailStudySets(id) {
      if (this.showDetailStudySets.includes(id)) {
        this.showDetailStudySets = this.showDetailStudySets.filter((x) => x != id)
      } else {
        this.showDetailStudySets.push(id)
      }
    },
    overtablePaginationFormatter(start, end, total) {
      return this.$t('information.show_from_to_of_total', {
        start: start,
        end: end,
        total: total
      })
    },

    showPharseOfStudySet(studySet) {
      if (!studySet || !studySet.phrases) {
        return 0
      }
      // let ret = []
      // for (let i = 0; i < Math.min(studySet.phrases.length, 5); i++) {
      //   ret.push(studySet.phrases[i])
      // }
      // return ret
      return studySet.phrases
    },
    changeStudySetChecked(studySet, status) {
      if (status) {
        this.listChecked = [...this.listChecked, studySet]
      } else {
        this.listChecked = this.listChecked.filter((x) => x.id !== studySet.id)
      }
    },
    checkChecked(studySet) {
      return this.listChecked.filter(item => item.id === studySet.id).length > 0
    }
  },
  mounted() {
    // this.searchStudySet('')
    this.loadPaginationStudySet()
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../scss/_colors.scss';

ul {
  list-style-type: none;
  padding-left: 0px;
  margin-bottom: 0px;
  margin-top: 0px;
}

.study-set-overview {
  font-size: 14px;
  height: 64px;
  align-items: center;

  .study-set-name {
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin-top: 0px;
    margin-bottom: 0px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    color: $dark-blue-2;
    text-align: initial;
    font-weight: 400;
  }

  .value {
    color: $black;
    text-transform: none;
    font-weight: 600;
  }
}

.study-set-overview-list {
  .study-set-overview-row-wrapper {
    box-shadow: inset 0px -1px 0px #e6e6e6;
  }

  > :last-child {
    margin-bottom: 0px;
    box-shadow: none;
  }

  max-height: 320px;
  overflow-y: auto;
}

#leave-modal-title {
  font-weight: 700;
  font-size: 22px;
  line-height: 32px;
  text-align: center;
  color: $black;
  margin: 0 auto;
}

#leave-modal-desc {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: $black;
  margin: 4px auto;
}

.elsa-button.confirm-delete,
.elsa-button.confirm-delete:hover {
  border-radius: 8px;
}

.elsa-button.confirm-cancel,
.elsa-button.confirm-cancel:hover {
  border-radius: 8px;
  padding-left: 16px;
  padding-right: 16px;
}

::v-deep {
  .el-form {
    padding-bottom: 0px;

    .el-form-item {
      height: 48px;

      .el-input {
        font-size: 16px;
        font-weight: 400;

        input {
          height: 100%;
        }
      }
    }
  }

  .el-checkbox__inner {
    width: 16px;
    height: 16px;
  }

  .el-checkbox__inner::after {
    height: 7px;
    left: 5px;
  }

  .el-dialog.elsa-modal {
    border-radius: 8px;

    .el-dialog__header,
    .el-dialog__body {
      //padding: 40px 40px 0 40px;
      // display: flex;
      background-color: white;
    }

    &.signup .el-dialog__body {
      padding: 0;
    }

    .el-dialog__header {
      border-top-left-radius: 16px;
      border-top-right-radius: 16px;
      min-height: 27.5px;
      padding: 20px 24px 12px;
    }

    .el-dialog__body {
      border-top: none;

      &>*:not(p):not(.info) {
        padding-bottom: 6px;
        display: flex;
        flex-direction: column;
      }
    }

    .el-dialog__footer {
      padding: 0 20px 48px 20px;
      justify-content: center;
    }
  }
}

::v-deep .el-dialog__body {
  padding-bottom: 0px !important;
  padding-top: 0px !important;
  margin-bottom: 32px;
  padding-left: 7px !important;
  padding-right: 7px !important;
}

::v-deep #add-study-set-modal-body {
  max-height: 570px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 0px !important;
  padding-top: 0px !important;
  padding-left: 17px !important;
  padding-right: 17px !important;
  margin-bottom: 32px;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #edf4ff;
    border-radius: 100px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c2c2c2;
    border-radius: 100px;
  }
}

.phrases-wrapper {
  background: #f1f1f1;
  border-radius: 8px;
  padding: 16px 0px;
}

.phrases-list {
  overflow: auto;
  padding-left: 0px;
  display: flex;
  flex-direction: column;
  padding: 0;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #edf4ff;
    border-radius: 100px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #c2c2c2;
    // box-shadow: inset 0 0 8px #C2C2C2;
    border-radius: 100px;
  }

  > :last-child {
    margin-bottom: 0px;
    box-shadow: none;
  }

  .el-row {
    width: 100%;
  }
}

.phrases-list li {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */
  color: $black;
  margin-bottom: 16px;
  display: flex;
}

h1 {
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: 0;
}

::v-deep .el-input-group__append {
  background-color: #31292900;
}

::v-deep .el-checkbox__inner {
  border-color: #828292 !important;
  border-radius: 4px !important;
}

.footer-pagination {

  .pagination {
    &__description {
      font-size: 14px;
    }

    &__control {
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }
}
</style>
